import { Hit } from 'instantsearch.js';
import React, { memo, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { NewProductCard } from '~/shared/components/NewProductCard';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import {
    _transformProductDetailsToDataLayer,
    getCategorySlug,
} from '~/shared/hooks/useEvents/helpers';
import { PROMOTION_EVENT_TYPE, SponsoredPromotionEventData } from '~/shared/hooks/useEvents/models';
import { useIsMobile } from '~/shared/hooks/useIsMobile';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { useCompareModeStore } from '~/shared/productComparison';
import { usePage } from '~/templates/pages';
import { _transformAlgoliaToProductDetails } from '../../Algoliga.utils';
import { ProductEventPayload } from '~/lib/salesforce/types';
import styles from './ProductHit.module.scss';

interface ProductHitProps {
    hit: Hit<TProductDetails>;
    position: number;
    listId: EventIdentifier;
    listName: EventIdentifier;
    isFavourite?(id: string): boolean;
    onFavourite?(product: TProductDetails): void;
    onBuyButtonClick?(identifier: EventIdentifier, data: ProductEventPayload): void;
}

/**
 * This wrapper will help prevent the ProductCard from re-rendering inside the ProductList
 * and reduce unnecessary recalculations and transformations.
 */
const ProductHit: React.FC<ProductHitProps> = ({
    hit,
    listId,
    listName,
    isFavourite,
    onBuyButtonClick,
    ...rest
}) => {
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const { data: frameData } = useFrame();
    const { breadcrumb } = usePage();
    const { promotionViewEvent, promotionClickEvent } = useEvents(frameData);
    const isMobile = useIsMobile();
    const isCompareModeActive = useCompareModeStore((state) => state.isActive);

    // On mobile the toggle only shows once "Sammenlign" mode is switched on next to the
    // filter button - there isn't room to show it on every card by default there. Desktop
    // always shows it.
    const showCompareToggle = !isMobile || isCompareModeActive;

    const product = useMemo(
        () =>
            _transformAlgoliaToProductDetails(
                hit,
                translate,
                convertPriceToLocale,
                frameData?.productSettings,
            ),
        [hit.id],
    );

    const sponsored = useMemo<SponsoredPromotionEventData | null>(() => {
        const categorySlug = breadcrumb ? getCategorySlug(breadcrumb) : '';

        if (!hit._rankingInfo?.promoted || !categorySlug || !product.EAN) return null;

        return {
            type: PROMOTION_EVENT_TYPE.SPONSORED_PLACEMENT,
            id: `sp_${categorySlug}_${product.EAN}`,
            creativeName: product.brand ?? '',
            creativeSlot: hit.__position.toString(),
            itemListId: listId,
            itemListName: listName,
            // Index 0 is the position within this event's items array, not the list
            product: _transformProductDetailsToDataLayer(product, 1, 0),
        };
    }, [hit, product, breadcrumb, listId, listName]);

    const { inView, ref } = useInView({
        threshold: 0,
        rootMargin: '0px 0px -200px 0px',
        triggerOnce: true,
        skip: !sponsored,
    });

    useEffect(() => {
        if (!inView || !sponsored) return;

        promotionViewEvent(sponsored);
    }, [inView, sponsored]);

    // Fires alongside the select_item NewProductCard pushes right after, gated the same way
    const handleSponsoredClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.defaultPrevented || !sponsored) return;

        promotionClickEvent(sponsored);
    };

    // TODO(IMC-231): Evaluate need for raptorIdentifier here.
    // const raptorIdentifier = listId.replace(/ /g, '').toLowerCase();

    return (
        // TODO(IMC-231): Evaluate need for raptorIdentifier here.
        // This results in similar "id" values on multiple elements which is not recommended.
        // <div className={styles.wrapper} id={raptorIdentifier}>
        <div ref={ref} className={styles.wrapper}>
            <NewProductCard
                className={styles.card}
                product={product}
                {...rest}
                showCompareToggle={showCompareToggle}
                imageLoading="lazy"
                trackingListId={listId}
                trackingListName={listName}
                isFavourite={isFavourite?.(product.id) ?? false}
                onButtonClick={onBuyButtonClick}
                onClick={handleSponsoredClick}
            />
        </div>
    );
};

export default memo(ProductHit);
